import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@mantine/core";
import { ReactComponent as Logo } from "@/assets/dinamo-logo.svg";
import { ReactComponent as GridViewIcon } from "@/assets/icons/view/grid.svg";
import { ReactComponent as ListViewIcon } from "@/assets/icons/view/list.svg";
import { ReactComponent as SingleViewIcon } from "@/assets/icons/view/single.svg";
import { isIssueWithinSubscription } from "@/helpers/issuesAndSubscriptions";
import SingleView from "@/components/User/Shared/SingleView/SingleView";
import GridView from "@/components/User/Shared/GridView/GridView";
import ListView from "@/components/User/Shared/ListView/ListView";
import { UserSubscription, Issue } from "@/api/types";
import { IssueApi } from "@/api/issue.api";
import { OrderApi } from "@/api/order.api";
import { UserApi } from "@/api/user.api";
import clsx from "clsx";
import "./Catalogue.css";

const Catalogue = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = searchParams.get("view") || "list";
  const [activeView, setView] = useState(initialView);
  const isSingleView = activeView === "single";
  const isListView = activeView === "list";
  const isGridView = activeView === "grid";

  useEffect(() => {
    setSearchParams({ view: activeView });
  }, [activeView, setSearchParams]);

  const [issues, setIssues] = useState<Issue[]>([]);
  const [flipbookOpened, setFlipbookOpened] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState("");
  const [mySubscriptions, setMySubscriptions] = useState<UserSubscription[]>();
  const [orderedIssues, setOrderedIssues] = useState<Issue[]>();
  const [myIssues, setMyIssues] = useState<Issue[]>([]);
  let flipbookContainer: HTMLElement | null = null;

  const openFlipbook = (pdfUrl: string) => {
    setSelectedPdf(pdfUrl);
    setFlipbookOpened(true);
  };

  const getIssues = async () => {
    const result = await IssueApi.getAllIssues();
    if (result) setIssues(result);
  };

  const getMyIssuesAndSubscription = async () => {
    const subscriptions = await UserApi.getMySubscriptionData();
    if (subscriptions) setMySubscriptions(subscriptions);
    const orderedIssues = await OrderApi.getMyOrderedIssues();
    if (orderedIssues) setOrderedIssues(orderedIssues);
  };

  const setMine = () => {
    const myIssues = issues.filter((issue) => {
      if (mySubscriptions?.length) {
        return (
          orderedIssues?.map((oi) => oi.id).includes(issue.id) ||
          isIssueWithinSubscription(issue, mySubscriptions)
        );
      } else return orderedIssues?.map((oi) => oi.id).includes(issue.id);
    });
    setMyIssues(myIssues);
  };

  useEffect(() => {
    if (issues?.length > 0) {
      setMine();
    }
  }, [issues, orderedIssues, mySubscriptions]);

  useEffect(() => {
    getIssues();
    getMyIssuesAndSubscription();
    if (flipbookOpened && selectedPdf) {
      initializeFlipBook(selectedPdf);
    }
    return () => {
      if (flipbookContainer) {
        flipbookContainer.remove();
        flipbookContainer = null;
      }
    };
  }, [flipbookOpened, selectedPdf]);

  const initializeFlipBook = (pdfUrl: string) => {
    flipbookContainer = document.createElement("div");
    flipbookContainer.id = "flipbook-container";
    document.body.appendChild(flipbookContainer);

    //@ts-ignore
    $(flipbookContainer).flipBook({
      pdfUrl,
      lightBox: true,
      layout: 3,
      currentPage: { vAlign: "bottom", hAlign: "left" },
      btnPrint: { enabled: false },
      btnDownloadPages: {
        enabled: false,
      },
      btnDownloadPdf: { enabled: false },
      btnColor: "#00509f",
      sideBtnColor: "#00509f",
      sideBtnSize: 60,
      sideBtnBackground: "rgba(0,0,0,.7)",
      sideBtnRadius: 60,
      btnSound: { vAlign: "top", hAlign: "left" },
      btnAutoplay: { vAlign: "top", hAlign: "left" },
      btnShare: {
        enabled: false,
      },
    });

    setTimeout(() => {
      flipbookContainer?.click();
    }, 100);

    flipbookContainer.addEventListener("click", (event) => {
      if (event.target === flipbookContainer) {
        closeFlipbook();
      }
    });
  };

  const closeFlipbook = () => {
    if (flipbookContainer) {
      flipbookContainer.remove();
      flipbookContainer = null;
    }
    setFlipbookOpened(false);
    setSelectedPdf("");
  };

  return (
    <div className="catalogue-container">
      <div className="catalogue-header">
        <div className="text-[#004A99] text-xl font-bold">Katalog revija</div>
        <Button.Group className="button-nav">
          <Button
            value="single"
            variant="filled"
            className={clsx(
              "!rounded-xl !h-[40px] !w-[50px] !p-0",
              isSingleView ? "!bg-white" : "view-button"
            )}
            onClick={() => setView("single")}
          >
            <SingleViewIcon
              width={20}
              height={20}
              className={isSingleView ? "text-[#d5c591]" : "text-white"}
            />
          </Button>
          <div className="flex items-center">
            <div className="w-0.5 h-4 bg-white rounded-sm" />
          </div>
          <Button
            value="list"
            variant="filled"
            className={clsx(
              "!rounded-xl !h-[40px] !w-[50px] !p-0",
              isListView ? "!bg-white" : "view-button"
            )}
            onClick={() => setView("list")}
          >
            <ListViewIcon
              width={20}
              height={20}
              className={isListView ? "text-[#d5c591]" : "text-white"}
            />
          </Button>
          <div className="flex items-center">
            <div className="w-0.5 h-4 bg-white rounded-sm" />
          </div>
          <Button
            value="grid"
            variant="filled"
            className={clsx(
              "!rounded-xl !h-[40px] !w-[50px] !p-0",
              isGridView ? "!bg-white" : "view-button"
            )}
            onClick={() => setView("grid")}
          >
            <GridViewIcon
              width={20}
              height={20}
              className={isGridView ? "text-[#d5c591]" : "text-white"}
            />
          </Button>
        </Button.Group>
      </div>
      <div className="catalogue-mobile-header">
        <div className="flex gap-3 items-center">
          <Logo width={24} height={24} />
          <div className="text-[#004a99] text-lg font-bold">
            Digitalne revije
          </div>
        </div>
      </div>
      {!issues || !issues.length ? (
        <div className="empty-issues-div">
          <span>Trenutno nema revija.</span>
        </div>
      ) : (
        <div className="catalogue-views">
          {isSingleView && (
            <SingleView
              type="issue"
              items={issues}
              myItems={myIssues}
              openFlipbook={openFlipbook}
            ></SingleView>
          )}
          {isListView && (
            <ListView
              type="issue"
              items={issues}
              myItems={myIssues}
              openFlipbook={openFlipbook}
            ></ListView>
          )}
          {isGridView && (
            <GridView
              type="issue"
              items={issues}
              myItems={myIssues}
              openFlipbook={openFlipbook}
            ></GridView>
          )}
        </div>
      )}
      <div className="catalogue-mobile-footer !z-50">
        {isGridView && (
          <Button
            value="single"
            variant="filled"
            className={clsx("!rounded-xl !h-[55px] !w-[55px]", "view-button")}
            onClick={() => setView("single")}
          >
            <SingleViewIcon width={20} height={20} className={"text-white"} />
          </Button>
        )}
        {isSingleView && (
          <Button
            value="list"
            variant="filled"
            className={clsx(
              "!rounded-xl !h-[55px] !w-[55px] !z-50",
              "view-button"
            )}
            onClick={() => setView("list")}
          >
            <ListViewIcon width={20} height={20} className={"text-white"} />
          </Button>
        )}
        {isListView && (
          <Button
            value="grid"
            variant="filled"
            className={clsx(
              "!rounded-xl !h-[55px] !w-[55px] !p-0",
              "view-button"
            )}
            onClick={() => setView("grid")}
          >
            <GridViewIcon width={20} height={20} className={"text-white"} />
          </Button>
        )}
      </div>
    </div>
  );
};

export default Catalogue;
